import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import GoogleIcon from "@/components/GoogleIcon";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = `Sign in — ${BRAND.name}`; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      const dest = res.user.role === "admin" ? "/admin" : (location.state?.from || "/dashboard");
      nav(dest, { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRhbnQlMjBoYW5kc2hha2V8ZW58MHx8fHwxNzgyMzA0MDc2fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/80 via-[#0A2540]/60 to-[#0F9D58]/30" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="" className="h-12 w-12 object-contain bg-white rounded-xl p-1.5" />
            <div>
              <div className="font-heading font-bold text-lg">Accurate Claims Services</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold">{BRAND.subline}</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Trusted by 500+ families</div>
            <h2 className="font-heading font-bold text-4xl leading-tight max-w-md">
              A claim is only as strong as the team behind it.
            </h2>
            <p className="mt-4 text-slate-200 max-w-md">
              Sign in to track your case, upload documents and read your expert opinion — anytime, anywhere.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="login-back-home" className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-brand font-semibold">
            ← {BRAND.short}
          </Link>
          <h1 className="font-heading font-bold text-4xl mt-6">Sign in</h1>
          <p className="text-sm text-slate-600 mt-2">Access your client portal or admin console.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Password</Label>
              <Input
                id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11"
              />
            </div>
            <button
              type="submit" disabled={loading} data-testid="login-submit-btn"
              className="btn-primary w-full text-sm"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={startGoogleAuth}
            data-testid="login-google-btn"
            className="mt-4 w-full h-11 inline-flex items-center justify-center gap-3 rounded-full border-2 border-slate-200 hover:border-navy transition-colors font-semibold text-sm text-slate-800 bg-white"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="mt-4 text-right">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-slate-500 hover:text-emerald-brand font-semibold">
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <a href={BRAND.telUrl} className="flex-1 btn-outline text-xs !py-2.5"><Phone className="w-3.5 h-3.5" /> Call</a>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="flex-1 btn-emerald text-xs !py-2.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
          </div>

          <div className="mt-8 border-t border-slate-200 pt-6 text-sm text-slate-600">
            No account yet?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-emerald-brand font-semibold hover:underline">
              Register as client →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
